import {
  EXPO_PUBLIC_PAYBOOKS_MIXPANEL_PROXY_DOMAIN,
  EXPO_PUBLIC_PAYBOOKS_MIXPANEL_PROJECT_TOKEN,
} from "@lib/constants";
import { NavigationState, PartialState } from "@react-navigation/native";
import { Mixpanel, MixpanelProperties } from "mixpanel-react-native";

/**
 * A singleton class to manage all Mixpanel interactions within the app.
 * This ensures that the Mixpanel instance is initialized only once and provides
 * a clear, safe API for tracking events.
 */
class MixpanelManager {
  private static instance: MixpanelManager;
  private mixpanel: Mixpanel | null = null;
  private isInitialized = false;

  // Private constructor to prevent direct instantiation.
  private constructor() {}

  /**
   * Gets the singleton instance of the MixpanelManager.
   */
  public static getInstance(): MixpanelManager {
    if (!MixpanelManager.instance) {
      MixpanelManager.instance = new MixpanelManager();
    }
    return MixpanelManager.instance;
  }

  /**
   * Initializes the Mixpanel instance.
   * Throws an error if environment variables are missing.
   * This must be called once when the app starts.
   */
  public async init(): Promise<void> {
    if (this.isInitialized) {
      console.warn("Mixpanel has already been initialized.");
      return;
    }

    if (!EXPO_PUBLIC_PAYBOOKS_MIXPANEL_PROJECT_TOKEN) {
      throw new Error("Mixpanel project token is not configured in environment variables.");
    }

    this.mixpanel = new Mixpanel(EXPO_PUBLIC_PAYBOOKS_MIXPANEL_PROJECT_TOKEN, false /* trackAutomaticEvents */, false /* useNative */);
    await this.mixpanel.init(false, {}, EXPO_PUBLIC_PAYBOOKS_MIXPANEL_PROXY_DOMAIN);
    this.isInitialized = true;
    console.log("Mixpanel initialized successfully.");
  }

  private getSafeInstance(): Mixpanel {
    if (!this.isInitialized || !this.mixpanel) {
      throw new Error("Mixpanel is not initialized. Call init() before using any other method.");
    }
    return this.mixpanel;
  }

  /**
   * Associates all future events with a specific user.
   * @param userId - A unique identifier for the user.
   */
  public identify(userId: string): void {
    this.getSafeInstance().identify(userId);
  }

  /**
   * Tracks a custom event with optional properties.
   * @param eventName - The name of the event.
   * @param properties - Additional properties to send with the event.
   */
  public track(eventName: string, properties?: MixpanelProperties): void {
    this.getSafeInstance().track(eventName, properties);
  }

  /**
   * React Navigation state change listener to automatically track screen views.
   * @param state - The navigation state from React Navigation.
   */
  private _onReactNavigationStateChange(state: NavigationState | PartialState<NavigationState> | undefined): void {
    const getActiveRouteName = (navState: NavigationState | PartialState<NavigationState> | undefined): string | undefined => {
      if (!navState) return undefined;
      const route = navState.routes[navState.index ?? 0];
      // Dive into nested navigators if they exist
      if (route.state) return getActiveRouteName(route.state);
      return route.name;
    };

    const currentRoute = getActiveRouteName(state);
    if (currentRoute) {
      this.track("Page View", { screen: currentRoute });
    }
  }

  public onReactNavigationStateChange = this._onReactNavigationStateChange.bind(this);

  public setPeople = (profile: any) => {
    this.getSafeInstance().getPeople().set(profile);
  }
}

export const mixpanel = MixpanelManager.getInstance();


// // Initialize mixpanel; it only send event after initialization
// export const init = mixpanel.init.bind(mixpanel);

// // Ensure all future events sent from the  device will have the same distinct_id
// export const identify = mixpanel.identify.bind(mixpanel);

// // Register a set of super properties, which are included with all events.
// export const register = mixpanel.registerSuperProperties.bind(mixpanel);

// export const people_set = mixpanel.getPeople().set.bind(mixpanel);

// // Clears super properties and generates a new random distinct_id
// // for this instance. Useful for clearing data when a user logs out.
// export const reset = mixpanel.reset.bind(mixpanel);

// // Manually, force all queued events at a particular moment.
// export const flush = mixpanel.flush.bind(mixpanel);


// Send a default page view event with additional properties
// export const track_pageview = async (properties: MixpanelProperties) => {
//   mixpanel.track("Page View", properties);
//   mixpanel.flush();
// };

// export const track = mixpanel.track.bind(mixpanel);
